(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => reduced.matches ? 'auto' : 'smooth';

  class TrahvlTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const panels = [...this.querySelectorAll('[role="tabpanel"]')];
      const activate = (tab, focus = false) => {
        tabs.forEach(item => {
          item.setAttribute('aria-selected', String(item === tab));
          item.tabIndex = item === tab ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          activate(tabs[next], true);
        });
      });
    }
  }

  class TrahvlCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const track = this.querySelector('[data-track]');
      if (!track) return;
      this.ready = true;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const originals = [...track.children];
      const count = originals.length;
      if (!count) return;
      const centered = this.hasAttribute('data-centered');
      const loop = this.hasAttribute('data-loop') && count > 1;
      this.clones = [];
      if (loop) {
        const copy = slide => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          [clone, ...clone.querySelectorAll('[id]')].forEach(node => node.removeAttribute('id'));
          [clone, ...clone.querySelectorAll('a, button, input, [tabindex]')].forEach(node => node.tabIndex = -1);
          this.clones.push(clone);
          return clone;
        };
        track.prepend(...originals.map(copy));
        track.append(...originals.map(copy));
      }
      const slides = [...track.children];
      const dots = [...this.querySelectorAll('[data-slide]')];
      const start = loop ? count : 0;
      let index = 0;
      let moving = false;
      const offset = slide => slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - (centered ? (track.clientWidth - slide.getBoundingClientRect().width) / 2 : 0);
      const mark = () => dots.forEach((dot, current) => dot.setAttribute('aria-current', String(current === index)));
      const stop = () => { cancelAnimationFrame(this.frame); moving = false; };
      const reset = () => {
        stop();
        if (track.clientWidth) track.scrollLeft = offset(slides[start + index]);
        mark();
      };
      const go = next => {
        stop();
        clearTimeout(this.settle);
        const physical = loop ? start + next : Math.max(0, Math.min(count - 1, next));
        index = loop ? (next + count) % count : physical;
        mark();
        const from = track.scrollLeft;
        const to = offset(slides[physical]);
        if (reduced.matches) { track.scrollLeft = to; reset(); return; }
        moving = true;
        const began = performance.now();
        const tick = now => {
          const progress = Math.min(1, (now - began) / 300);
          track.scrollLeft = from + (to - from) * (1 - Math.pow(1 - progress, 3));
          if (progress < 1) this.frame = requestAnimationFrame(tick);
          else reset();
        };
        this.frame = requestAnimationFrame(tick);
      };
      this.querySelector('[data-prev]')?.addEventListener('click', () => go(index - 1), options);
      this.querySelector('[data-next]')?.addEventListener('click', () => go(index + 1), options);
      dots.forEach(dot => dot.addEventListener('click', () => go(Number(dot.dataset.slide)), options));
      let drag;
      let dragged = false;
      track.addEventListener('pointerdown', event => {
        stop();
        dragged = false;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, left: track.scrollLeft };
      }, options);
      track.addEventListener('pointermove', event => {
        if (!drag) return;
        const distance = event.clientX - drag.x;
        if (Math.abs(distance) > 6) {
          dragged = true;
          track.setPointerCapture(event.pointerId);
          event.preventDefault();
          track.scrollLeft = drag.left - distance;
        }
      }, options);
      const finishDrag = () => {
        if (!drag) return;
        drag = null;
        track.dispatchEvent(new Event('scroll'));
      };
      track.addEventListener('pointerup', finishDrag, options);
      track.addEventListener('pointercancel', finishDrag, options);
      track.addEventListener('pointerleave', finishDrag, options);
      track.addEventListener('dragstart', event => event.preventDefault(), options);
      track.addEventListener('click', event => {
        if (dragged) { event.preventDefault(); event.stopPropagation(); dragged = false; }
      }, { ...options, capture: true });
      track.addEventListener('wheel', stop, { ...options, passive: true });
      track.addEventListener('scroll', () => {
        if (moving || drag) return;
        clearTimeout(this.settle);
        this.settle = setTimeout(() => {
          const nearest = slides.reduce((best, slide, current) => Math.abs(offset(slide) - track.scrollLeft) < Math.abs(offset(slides[best]) - track.scrollLeft) ? current : best, 0);
          index = (nearest + count - start) % count;
          if (loop && Math.abs(offset(slides[start + index]) - track.scrollLeft) > 1) reset();
          else mark();
        }, 150);
      }, { ...options, passive: true });
      this.resize = new ResizeObserver(reset);
      this.resize.observe(track);
      reduced.addEventListener('change', reset, options);
      reset();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settle);
      this.clones?.forEach(clone => clone.remove());
      this.ready = false;
    }
  }

  class TrahvlCampaign extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.button = this.querySelector('[data-video-toggle]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.video.muted = true;
      this.video.playsInline = true;
      this.inView = false;
      this.pausedByUser = false;
      this.update = () => {
        if (this.inView && !document.hidden && !reduced.matches && !this.pausedByUser) {
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      };
      this.button?.addEventListener('click', () => {
        if (this.video.paused) {
          this.pausedByUser = false;
          this.video.play().catch(() => {});
        } else {
          this.pausedByUser = true;
          this.video.pause();
        }
      }, options);
      this.video.addEventListener('play', () => {
        if (this.button) this.button.textContent = 'Pause film';
      }, options);
      this.video.addEventListener('pause', () => {
        if (this.button) this.button.textContent = 'Play film';
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.update();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', this.update);
      reduced.addEventListener('change', this.update);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      if (this.update) {
        document.removeEventListener('visibilitychange', this.update);
        reduced.removeEventListener('change', this.update);
      }
      this.video?.pause();
      this.ready = false;
    }
  }

  if (!customElements.get('trahvl-tabs')) customElements.define('trahvl-tabs', TrahvlTabs);
  if (!customElements.get('trahvl-carousel')) customElements.define('trahvl-carousel', TrahvlCarousel);
  if (!customElements.get('trahvl-campaign')) customElements.define('trahvl-campaign', TrahvlCampaign);

  document.addEventListener('click', event => {
    const cue = event.target.closest('[data-scroll-next]');
    if (!cue) return;
    const next = cue.closest('.shopify-section')?.nextElementSibling;
    next?.scrollIntoView({ behavior: behavior(), block: 'start' });
  });

  let headerResize;
  let revealObserver;
  let reviewObserver;
  let scrollFrame;
  let header;
  let parallax = [];
  let navLinks = [];
  const updateScroll = () => {
    scrollFrame = null;
    header?.classList.toggle('is-scrolled', window.scrollY > 20);
    parallax.forEach(media => {
      const image = media.querySelector('img');
      if (!image || reduced.matches) return;
      const box = media.getBoundingClientRect();
      const height = image.getBoundingClientRect().height;
      const centered = box.top + (box.height - height) / 2;
      const drift = (innerHeight / 2 - box.top - box.height / 2) * .18;
      const top = Math.min(box.top, Math.max(box.bottom - height, centered + drift));
      media.style.setProperty('--trahvl-parallax-y', `${top}px`);
    });
    const threshold = (header?.getBoundingClientRect().height || 100) + 40;
    let active = navLinks[0];
    navLinks.forEach(link => {
      const target = document.getElementById(link.hash.slice(1));
      if (target && target.getBoundingClientRect().top <= threshold) active = link;
    });
    navLinks.forEach(link => link.setAttribute('aria-current', String(link === active)));
  };
  const queueScroll = () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
  };
  const setupPage = () => {
    headerResize?.disconnect();
    header = document.querySelector('.trahvl-header');
    if (header) {
      const measure = () => {
        document.body.style.setProperty('--trahvl-header-height', `${header.getBoundingClientRect().height}px`);
        queueScroll();
      };
      headerResize = new ResizeObserver(measure);
      headerResize.observe(header);
      measure();
    }
    parallax = [...document.querySelectorAll('[data-trahvl-parallax]')];
    parallax.forEach(media => media.classList.toggle('is-parallax', !reduced.matches));
    navLinks = [...document.querySelectorAll('.trahvl-faq__nav a[href^="#"], .trahvl-collection-faq__nav a[href^="#"]')];
    revealObserver?.disconnect();
    const faq = document.querySelector('.trahvl-faq');
    const targets = faq ? [faq.querySelector('.trahvl-faq__grid')] : [...document.querySelectorAll('.monos-title, .monos-tile, .monos-card:not([aria-hidden]), .monos-feature__body, .monos-trust__grid, .trahvl-social__post, .trahvl-category-card, .trahvl-product-group__heading, .trahvl-collection-articles__heading, .trahvl-collection-article')];
    revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('is-pending');
      revealObserver.unobserve(entry.target);
    }), { threshold: .05 });
    targets.filter(Boolean).forEach(target => {
      target.classList.add('trahvl-reveal');
      target.classList.toggle('is-pending', !reduced.matches && target.getBoundingClientRect().top > innerHeight);
      if (!reduced.matches) revealObserver.observe(target);
    });
    reviewObserver?.disconnect();
    const empty = document.querySelector('[data-review-empty]');
    if (empty) {
      const area = empty.parentElement;
      const updateReviews = () => { empty.hidden = !!area.querySelector('.jdgm-rev, .jdgm-prev-badge[data-number-of-reviews]:not([data-number-of-reviews="0"])'); };
      reviewObserver = new MutationObserver(updateReviews);
      reviewObserver.observe(area, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-number-of-reviews'] });
      updateReviews();
    }
    updateScroll();
  };
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll, { passive: true });
  reduced.addEventListener('change', setupPage);
  document.addEventListener('shopify:section:load', setupPage);
  window.addEventListener('pagehide', () => {
    headerResize?.disconnect();
    revealObserver?.disconnect();
    reviewObserver?.disconnect();
    cancelAnimationFrame(scrollFrame);
  });
  window.addEventListener('pageshow', setupPage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupPage, { once: true });
  else setupPage();
})();
